import {UsuarioInfoTextField} from '@components/contas/UsuarioInformacao.tsx';
import {useAlert} from '@contexts/hooks/useAlert.ts';
import {useGeneral} from '@contexts/GeneralContext.tsx';
import {_Sitemap} from '@localization/siteMap/breadcrumbs/_Sitemap.ts';
import {_fornecedor_endpoints} from '@components/fornecedores/endpoints/_fornecedor_endpoints.ts';
import {DefaultInformationProps, DefaultNavLinkProps} from '@models/DefaultProps.ts';
import type {IFornecedor} from "@models/fornecedores/fornecedor.ts";
import {Button, Grid, TableCell, TableRow, TextField} from "@mui/material";
import {DateTimeField} from '@mui/x-date-pickers';
import {NavigationLink} from '@shared/custom/NavigationLink.tsx';
import {TextFieldNav} from '@shared/custom/TextFieldNav.tsx';
import {ToInt} from '@shared/MathHelp.ts';
import {ToCpfCnpj} from '@shared/ValidationHelp.ts';
import dayjs from 'dayjs';
import React, {useEffect, useState} from "react";
import {useApi} from "@contexts/hooks/api/useApi.ts";

interface FornecedorInformacaoProps extends DefaultInformationProps<IFornecedor> {
}

export function FornecedorInformacaoFromId(props: Omit<FornecedorInformacaoProps, 'value'> & {
    value: number,
    label?: string
} & Omit<DefaultNavLinkProps<IFornecedor, 'nome' | 'cnpj'>, 'value'>) {
    const {Get} = useApi();
    const {value} = props

    const [f, setF] = useState<IFornecedor>();

    useEffect(() => {
        Get(_fornecedor_endpoints.GetFornecedores({Ids: [value]}, {page: 1, pageSize: 1})).then(x => {
            if (x.hasData && x.items.length > 0) {
                setF(x.items[0]);
            } else {
                setF(undefined);
            }
        })
    }, [value]);

    return (f && <FornecedorInformacao {...props} value={f}/>)
}


export const FornecedorInformacao: React.FC<FornecedorInformacaoProps> = (props) => {
    const {value, variant} = props;
    if (variant === 'grid') return <FornecedorInfoGrid value={value}/>;
    if (variant === 'row') return <FornecedorInfoRow {...props}/>;
    if (variant === 'navigationLink') return <FornecedorInfoNavLink {...props}/>;
    if (variant === 'list') return <FornecedorInfoList {...props}/>;
    if (variant === 'textfield') return <FornecedorInfoTextfieldNav {...props} displayMode={'nome'} value={value}/>

    return (
        <div>
            <h1>Fornecedor Informações</h1>
            <p>Informações detalhadas sobre o fornecedor selecionado.</p>
        </div>
    );
};

export const FornecedorInfoList: React.FC<Omit<FornecedorInformacaoProps, 'variant' | 'overrides'>> = (props) => {
    const {value, EndComponent} = props;

    return (
        <Grid container>
            <Grid size={'grow'}>
                <Grid container spacing={1}>
                    <FornecedorInfoNavLink {...props}/>
                    <FornecedorInfoNavLink {...props} displayMode={'cnpj'}/>
                </Grid>
            </Grid>
            {EndComponent && <Grid><EndComponent {...value}/></Grid>}
        </Grid>
    );
};

export const FornecedorInfoGrid: React.FC<Omit<FornecedorInformacaoProps, 'variant' | 'overrides'> & {
    onUpdate?: (f: IFornecedor) => void;
    readonly?: boolean;
}> = (props) => {
    const {value} = props;
    const {adjustSizeLevel, pickCompare} = useGeneral();
    const {createAlertFromResponse} = useAlert();
    const {Get} = useApi();
    const [newValue, setNewValue] = React.useState<IFornecedor>(value);

    const handleUpdate = () => {
        Get(_fornecedor_endpoints.UpdateFornecedor(newValue)).then(x => {
            if (x.hasData) {
                setNewValue(x.items);
                if (props.onUpdate) props.onUpdate(x.items);
            }
            createAlertFromResponse(x);
        });
    };

    return (
        <Grid container rowSpacing={1}>
            <Grid size={12}>
                <Grid container justifyContent={'space-between'}>
                    <Grid/>
                    <Grid>
                        <Button variant={'outlined'}
                                onClick={handleUpdate}
                                disabled={pickCompare(value, newValue, ['nome', 'nomeFantasia', 'inscricaoEstadual', 'idErp'])}>Salvar</Button>
                    </Grid>
                </Grid>
            </Grid>

            <Grid size={2}>
                <TextField value={value.id} disabled label={'Id'} fullWidth/>
            </Grid>
            <Grid size={2}>
                <TextField value={newValue.idErp} disabled={props.readonly} label={'Id Erp'}
                           onChange={e => setNewValue({...newValue, idErp: ToInt(e.target.value)})}
                           fullWidth/>
            </Grid>
            <Grid size={8}>
                <TextField value={newValue.nome}
                           fullWidth
                           disabled={props.readonly}
                           label={'Nome'}
                           onChange={e => setNewValue({...newValue, nome: e.target.value})}
                />
            </Grid>
            <Grid size={adjustSizeLevel({sm: 12, md: 6})}>
                <TextField value={newValue.nomeFantasia}
                           label={'Nome Fantasia'}
                           disabled={props.readonly}
                           fullWidth
                           onChange={e => setNewValue({...newValue, nomeFantasia: e.target.value})}
                />
            </Grid>
            <Grid size={adjustSizeLevel({sm: 12, md: 6})}>
                <TextField value={newValue.inscricaoEstadual}
                           label={'Inscrição estadual'}
                           disabled={props.readonly}
                           fullWidth
                           onChange={e => setNewValue({...newValue, inscricaoEstadual: e.target.value})}
                />
            </Grid>
            <Grid size={12}>
                <FornecedorInfoTextfieldNav label={'Transportadora'} displayMode={'cpfCnpj'} value={value}/>
            </Grid>
            {value.usuarioCriacao && <Grid size={adjustSizeLevel({sm: 12, md: 6})}>
                <UsuarioInfoTextField label={'Criado por'} value={value.usuarioCriacao}/>
            </Grid>}
            <Grid size={adjustSizeLevel({sm: 12, md: value.usuarioCriacao ? 6 : 12})}>
                <DateTimeField fullWidth disabled label={'Criado em'} value={dayjs(value.dataCadastro)}/>
            </Grid>


        </Grid>
    );
};

export const FornecedorInfoTextfieldNav: React.FC<Omit<FornecedorInformacaoProps, 'variant' | 'overrides'> & {
    label?: string
} & DefaultNavLinkProps<IFornecedor, keyof IFornecedor
>> = (props) => {
    const {value, displayMode, label} = props;

    return (
        <TextFieldNav pathName={_Sitemap.fornecedores.children.detalhes.fullUrl}
                      value={value}
                      label={label ?? 'Fornecedor'}
                      displayValue={displayMode == 'cpfCnpj' ? ToCpfCnpj(value.cpfCnpj) : undefined}
                      displayMode={displayMode}
                      searchData={{fornecedorId: value.id}}
        />
    );
};

export const FornecedorInfoNavLink: React.FC<Omit<FornecedorInformacaoProps, 'variant' | 'overrides' | 'EndComponent'> & DefaultNavLinkProps<IFornecedor, 'nome' | 'cnpj'>> = (props) => {
    const {value, displayMode} = props;

    return (
        <NavigationLink {...props}
                        pathName={_Sitemap.fornecedores.children.detalhes.fullUrl}
                        chipSx={props.slotProps?.chip?.sx}
                        tableMode={props.slotProps?.chip?.tableMode}
                        searchData={{fornecedorId: value.id}}
                        toolTipHint={'Ir para detalhes do fornecedor ' + value.nome}
                        label={displayMode == 'cnpj' ? (ToCpfCnpj(value.cpfCnpj) ?? '') : value.nome}
        />
    );
};

export const FornecedorInfoRow: React.FC<Omit<FornecedorInformacaoProps, 'variant' | 'overrides'>> = (props) => {
    const {value, EndComponent} = props;

    return (<TableRow>
        <TableCell>{value.id}</TableCell>
        <TableCell><FornecedorInfoNavLink {...props} slotProps={{
            chip: {
                tableMode: true,
            },
        }}/></TableCell>
        <TableCell><FornecedorInfoNavLink {...props} displayMode={'cnpj'} slotProps={{
            chip: {
                tableMode: true,
            },
        }}/></TableCell>
        <TableCell align={'right'}>{EndComponent && <EndComponent {...value}/>}</TableCell>
    </TableRow>);
};